/* eslint-disable fp/no-mutation */
import {
  Typography,
  Container,
  Box,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Tab,
  Tabs,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { BarChart, PieChart } from '@mui/x-charts'
import { nullSafeValueFormatter } from 'src/v3/utilities/charts'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchLicenseTypeDemographics,
  fetchLicenseStateDemographics,
  selectLicenseTypeDemographics,
  selectLicenseStateDemographics,
  selectLicenseTypeDemographicsStatus,
  selectLicenseStateDemographicsStatus,
} from './scribe.slice'

type DemographicBreakdown = {
  licenseType?: string
  licenseState?: string
  count: number
}

export default () => {
  let dispatch = useAppDispatch()
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [viewMode, setViewMode] = useState<'licenseType' | 'licenseState'>(
    'licenseType',
  )
  let [chartTab, setChartTab] = useState('all')
  let [showOther, setShowOther] = useState(true)

  let licenseTypeDemographics = useAppSelector(selectLicenseTypeDemographics)
  let licenseStateDemographics = useAppSelector(selectLicenseStateDemographics)
  let licenseTypeStatus = useAppSelector(selectLicenseTypeDemographicsStatus)
  let licenseStateStatus = useAppSelector(selectLicenseStateDemographicsStatus)

  let loading =
    (viewMode === 'licenseType' ? licenseTypeStatus : licenseStateStatus) ===
    'pending'

  useEffect(() => {
    dispatch(fetchLicenseTypeDemographics())
    dispatch(fetchLicenseStateDemographics())
  }, [dispatch])

  useEffect(() => {
    if (licenseTypeStatus === 'rejected') {
      setErrorMessage('License type demographics failed to load')
    }
    if (licenseStateStatus === 'rejected') {
      setErrorMessage('License state demographics failed to load')
    }
  }, [licenseTypeStatus, licenseStateStatus])

  let getCurrentData = () => {
    let data
    if (viewMode === 'licenseType') {
      data =
        licenseTypeDemographics[
          chartTab as keyof typeof licenseTypeDemographics
        ] || []
    } else {
      data =
        licenseStateDemographics[
          chartTab as keyof typeof licenseStateDemographics
        ] || []
    }

    if (!showOther) {
      return data.filter(
        (item) => item.licenseType !== 'Other' && item.licenseState !== 'Other',
      )
    }
    return data
  }

  let renderChart = (data: DemographicBreakdown[]) => {
    if (!data || data.length === 0) return null

    let chartData = data.map((item, index) => ({
      id: index,
      value: item.count,
      label: item.licenseType || item.licenseState || 'Unknown',
    }))

    let barData = data.map((item) => item.count)
    let barLabels = data.map(
      (item) => item.licenseType || item.licenseState || 'Unknown',
    )

    return (
      <>
        <Box sx={{ mb: 4 }}>
          <BarChart
            margin={{
              bottom: barLabels.length > 8 ? 120 : 80,
              left: 60,
              right: 60,
              top: 20,
            }}
            height={400}
            series={[
              {
                data: barData,
                label: `Count by ${viewMode === 'licenseType' ? 'License Type' : 'License State'}`,
                valueFormatter: nullSafeValueFormatter,
              },
            ]}
            xAxis={[
              {
                scaleType: 'band',
                data: barLabels,
                tickLabelStyle: {
                  angle: barLabels.length > 8 ? -45 : 0,
                  textAnchor: barLabels.length > 8 ? 'end' : 'middle',
                },
              },
            ]}
            yAxis={[{ min: 0 }]}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <PieChart
            height={400}
            series={[{ data: chartData }]}
            hideLegend={true}
          />
        </Box>
      </>
    )
  }

  let getTotalCount = (data: DemographicBreakdown[]) =>
    data?.reduce((sum, item) => sum + item.count, 0) || 0

  let getFilteredData = (category: string) => {
    let data
    if (viewMode === 'licenseType') {
      data =
        licenseTypeDemographics[
          category as keyof typeof licenseTypeDemographics
        ] || []
    } else {
      data =
        licenseStateDemographics[
          category as keyof typeof licenseStateDemographics
        ] || []
    }

    if (!showOther) {
      return data.filter(
        (item) => item.licenseType !== 'Other' && item.licenseState !== 'Other',
      )
    }
    return data
  }

  return (
    <>
      <Container maxWidth="xl">
        {!loading ? (
          <>
            <Box className="grid grid-cols-6">
              <Box className="col-span-6 2xl:col-span-3">
                <Box>
                  <Typography
                    variant="h1"
                    sx={{ mb: 0, lineHeight: 'inherit' }}
                  >
                    User Demographics
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    User breakdown by license type and state
                  </Typography>
                </Box>
              </Box>
              <Box
                className="col-span-6 2xl:col-span-3"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  mb: 2,
                  mt: 3,
                }}
              >
                <ToggleButtonGroup
                  value={showOther ? 'show' : 'hide'}
                  exclusive
                  onChange={(_, newValue) =>
                    newValue && setShowOther(newValue === 'show')
                  }
                  size="small"
                  sx={{ mr: 2 }}
                >
                  <ToggleButton value="show">Show Other</ToggleButton>
                  <ToggleButton value="hide">Hide Other</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, newValue) => newValue && setViewMode(newValue)}
                  size="small"
                >
                  <ToggleButton value="licenseType">License Type</ToggleButton>
                  <ToggleButton value="licenseState">
                    License State
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            <Box className="grid grid-cols-12 gap-4 mt-4">
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">All Users</Typography>
                    <Typography variant="h2">
                      {getTotalCount(getCurrentData())}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">Active Users</Typography>
                    <Typography variant="h2">
                      {getTotalCount(getFilteredData('active'))}
                    </Typography>
                    <Typography variant="body2">
                      Active in last 6 months
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">Subscribed Users</Typography>
                    <Typography variant="h2">
                      {getTotalCount(getFilteredData('subscribed'))}
                    </Typography>
                    <Typography variant="body2">
                      Individual paid subscriptions
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">Linked Service Users</Typography>
                    <Typography variant="h2">
                      {getTotalCount(getFilteredData('linkedService'))}
                    </Typography>
                    <Typography variant="body2">
                      Organization sponsored
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box className="col-span-12">
                <Tabs
                  variant="scrollable"
                  value={chartTab}
                  onChange={(e, val) => setChartTab(val)}
                >
                  <Tab value="all" label="All Users" />
                  <Tab value="active" label="Active Users" />
                  <Tab value="subscribed" label="Subscribed Users" />
                  <Tab value="linkedService" label="Linked Service Users" />
                </Tabs>
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {chartTab === 'all' && 'All Users'}
                      {chartTab === 'active' && 'Active Users'}
                      {chartTab === 'subscribed' && 'Subscribed Users'}
                      {chartTab === 'linkedService' && 'Linked Service Users'}
                      {' by '}
                      {viewMode === 'licenseType'
                        ? 'License Type'
                        : 'License State'}
                    </Typography>
                    {renderChart(getCurrentData())}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </>
        ) : (
          <Box className="grid grid-cols-12 gap-4 mt-4">
            <Box className="col-span-6 2xl:col-span-3">
              <Box>
                <Typography variant="h1" sx={{ mb: 0, lineHeight: 'inherit' }}>
                  <Skeleton />
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  <Skeleton />
                </Typography>
              </Box>
            </Box>
            <Box
              className="col-span-6 2xl:col-span-3"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mb: 2,
                mt: 3,
              }}
            >
              <Skeleton
                sx={{
                  width: 200,
                  height: 40,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton
                sx={{
                  height: 136,
                }}
              />
            </Box>
            <Box className="col-span-12">
              <Skeleton
                sx={{
                  height: 600,
                }}
              />
            </Box>
          </Box>
        )}
      </Container>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
